import EventEmitter, { IBleEventArgs } from "./EventEmitter";
import PollingService from "./PollingService";
import { sendMessageAsync } from "./extensionUtils";
import bleExtensionMessageType from "../../consts/bleExtensionMessageType";
import BleDeviceService from "./BleDeviceService";
import { BleDeviceConnectionStatus, IBleDeviceConnectionStatusResponse, IBleDeviceDescription, IBleDeviceServicesResponse } from "./Models";
import bleEventsType from "../../consts/bleEventsType";
import bleDeviceConnectionStatus from "../../consts/bleDeviceConnectionStatus";
import BLE_UUID_TYPES from "../../consts/bleUuidTypes.js";
import errorService from "../ble/errors/errorService";
import objUtils from "../../utils/objUtils";

interface IConnectionStatusEventData {
    connectionStatus: BleDeviceConnectionStatus
}

export default class BleDeviceClient {
    private services: {[key: string]: BleDeviceService} = {}
    private deviceDescription: IBleDeviceDescription;
    private connectionStatus: BleDeviceConnectionStatus;
    private connectionStatusEmitter = new EventEmitter<IConnectionStatusEventData>();
    private connectionPoll = new PollingService();

    private fwuDataCharacteristic: any;
    private fwuControlCharacteristic: any;
    private fwuStatusCharacteristic: any;

    constructor(device: IBleDeviceDescription) {
        if(!device) throw new Error('Missing argument: "device"');
        this.deviceDescription = device;
        this.connectionStatus = this.deviceDescription.connectionStatus;
        if(this.connectionStatus === BleDeviceConnectionStatus.CONNECTED) {
            this.setRunConnectionStatus(true);
        }
    }

    getConnectionStatus(): BleDeviceConnectionStatus {
        return this.connectionStatus;
    }

    isDeviceConnected(): boolean {
        return this.connectionStatus === BleDeviceConnectionStatus.CONNECTED;
    }

    subscribeOnConnectionStatusChanged(handler: (arg0: IBleEventArgs<IConnectionStatusEventData>) => void) {
        this.connectionStatusEmitter.subscribe(bleEventsType.connectionChanged, handler);
    }

    unsubscribeOnConnectionStatusChanged(handler: (arg0: IBleEventArgs<IConnectionStatusEventData>) => void) {
        this.connectionStatusEmitter.unsubscribe(bleEventsType.connectionChanged, handler);
    }

    async connect() {        
        const sourceUrl = this.getSourceUrl();
        await sendMessageAsync<any>({type: bleExtensionMessageType.connect, id: this.deviceDescription.id, name: this.deviceDescription.name, sourceUrl: sourceUrl})
        let resolve: () => void;
        let reject: (error: Error) => void;
        const onStatusChanged = (event: IBleEventArgs<IConnectionStatusEventData>) => {
            if(event.data.connectionStatus === BleDeviceConnectionStatus.CONNECTED) {
                resolve();
            } else if(event.data.connectionStatus === BleDeviceConnectionStatus.DISCONNECTED) {
                reject(new Error("Device status changed to disconnected during connect"));
            }
        }
        
        const promise = new Promise<void>((res, rej) => {
            resolve = res;
            reject = rej;           
        })

        this.connectionStatusEmitter.subscribe(bleEventsType.connectionChanged, onStatusChanged);
        this.setRunConnectionStatus(true);

        try {
            await promise;
        } finally {
            this.connectionStatusEmitter.unsubscribe(bleEventsType.connectionChanged, onStatusChanged);
        }
    }

    async getServices(){

        // if (objUtils.isAnyKeys(this.services)) return [];

        const response = await sendMessageAsync<IBleDeviceServicesResponse>({type: bleExtensionMessageType.services, id: this.deviceDescription.id});

        if (!response?.services) return [];

        response.services.forEach((service) => {
            const {id, isPrimary} = service;
            const serviceUuid = id.toUpperCase();

            if (!this.services[serviceUuid]) {
                this.services[serviceUuid] = new BleDeviceService(this.deviceDescription.id, serviceUuid); 
            }
        })

        return response.services;     
    }

    async getCharacteristic(service: string, characteristic: string) {
        if(!Object.keys(this.services).length) {
            await this.getServices();
        }

        const serviceInstance = this.services[service.toUpperCase()];
        if(!serviceInstance) {
            throw new Error(`[${this.deviceDescription.id}]. Service [${service}] not exists`);
        }

        return serviceInstance.getCharacteristic(characteristic);
    }

    async getFwuServices() {
        this.fwuDataCharacteristic = await this.getCharacteristic(BLE_UUID_TYPES.RRP_SERVICE ,BLE_UUID_TYPES.FW_UPGRADE_DATA);
        this.fwuControlCharacteristic = await this.getCharacteristic(BLE_UUID_TYPES.RRP_SERVICE ,BLE_UUID_TYPES.FW_UPGRADE_CONTROL);
        this.fwuStatusCharacteristic = await this.getCharacteristic(BLE_UUID_TYPES.RRP_SERVICE ,BLE_UUID_TYPES.FW_UPGRADE_STATUS);
    };

    getFwuDataCharacteristic() {
        return this.fwuDataCharacteristic;
    }

    writeValueToFwuDataCharacteristic = (frame: string): Promise<void> =>
        this.writeValueToFwuCharacteristic(this.fwuDataCharacteristic, frame);

    writeValueToFwuControlCharacteristic = (frame: string): Promise<void> =>
        this.writeValueToFwuCharacteristic(this.fwuControlCharacteristic, frame);

    readFwuDataCharacteristic = (handler: Function): Promise<void> =>
        this.readCharacteristic(this.fwuDataCharacteristic, handler);
    
    readErrorValueFromCharacteristic = (handler: Function): Promise<void> =>
        this.readErrorValue(this.fwuDataCharacteristic, handler);

    async disconnect(): Promise<void> {
        this.stopReadingCharacteristics();
        if(this.connectionStatus === bleDeviceConnectionStatus.DISCONNECTED) {
            this.setRunConnectionStatus(false);
            return;
        }
        
        let resolve: () => void;
        let reject: (error: Error) => void;
        let timeout: any;

        const onStatusChanged = (event: IBleEventArgs<IConnectionStatusEventData>) => {
            if(event.data.connectionStatus === BleDeviceConnectionStatus.DISCONNECTED) {
                resolve();
                clearTimeout(timeout);
            }
        }
        
        const promise = new Promise<void>((res, rej) => {
            resolve = res;
            reject = rej;
                timeout = setTimeout(() => {rej(new Error("Disconnect timeout error"))}, 10000)
        })

        this.connectionStatusEmitter.subscribe(bleEventsType.connectionChanged, onStatusChanged);

        try {
            await sendMessageAsync<any>({type: bleExtensionMessageType.disconnect, id: this.deviceDescription.id}, 5000);
            await promise;
        } finally {
            this.connectionStatusEmitter.unsubscribe(bleEventsType.connectionChanged, onStatusChanged);
            this.setRunConnectionStatus(false);
            this.setNewConnectionStatus(BleDeviceConnectionStatus.DISCONNECTED);
        }
    }

    private setRunConnectionStatus(isRunning: boolean): void {
        if(isRunning) {
            this.connectionPoll.startPoll(() => {this.checkConnectionStatus()}, 2000);
        } else {
            this.connectionPoll.stopPoll();
        }
        
    }

    private getSourceUrl() {
       return window.location.origin;
    }

    private async checkConnectionStatus(): Promise<void> {
        const {connectionStatus} = await sendMessageAsync<IBleDeviceConnectionStatusResponse>({type: bleExtensionMessageType.connectionStatus, id: this.deviceDescription.id});

        this.setNewConnectionStatus(connectionStatus);
    }

    private setNewConnectionStatus(status: BleDeviceConnectionStatus): void {
        if(this.connectionStatus !== status) {
            this.connectionStatus = status;
            this.connectionStatusEmitter.emit(bleEventsType.connectionChanged, {data: {connectionStatus: status}})
        }
    }

    private stopReadingCharacteristics() {
        Object.values(this.services).forEach(service => service.stopReadingData());
    }

    readCharacteristic = async (characteristic: any, handler: Function) => {
        try {
            console.debug('BLEClient: try to read characteristic');
            await characteristic.triggerReadValue();


            const value = await characteristic.getCharacteristicValue();

            handler(value);
        } catch (e) {
            console.debug(
                `BLEClient: read characteristic error: ${errorService.getErrorMessage(e)}`
            );
        }
    };

    writeValueToFwuCharacteristic = async (characteristic: any, frame: string) => {
        try {
            
            await characteristic.writeValue(frame);
                        
        } catch (e) {
            console.debug(
                `BLEClient: frame: ${frame}, writeValueToCharacteristic error: ${errorService.getErrorMessage(e)}`
            );
        }
    };

    readErrorValue = async (characteristic: any, handler: Function) => {
        try {
            const error = await characteristic.readCharacteristicWriteValueErrorInternal();
            
            handler(error)
        } catch (error) {
            throw new Error("readErrorValue: Error while reading error value from characteristic");
        }
    }
}